import { withCache } from '@ngneat/cashew';

import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { environment } from '@environments/environment';

import { QueryBuilder } from '@cxc/core/builders';

import { OUT_OF_STANDARD_CURRENCY } from '@cxc/core/enums';

import { API } from '@models/API/index';

import { platformAPI } from '@services/API/endpoints';
import { OperationsState } from '@services/state/operation-state/operation.state';

enum INSIGHTS_CHARTS {
  ACCOUNT_BALANCE = 'ACCOUNT_BALANCE',
  AGING = 'AGING',
  ASSETS = 'ASSETS',
  BORROWING_BASE = 'BORROWING_BASE',
  COLLECTION = 'COLLECTION',
  OVERCOLLATERALIZATION = 'OVERCOLLATERALIZATION',
}

@Injectable({ providedIn: 'root' })
export class InsigthsAPI {
  constructor(
    private http: HttpClient,
    private operationsState: OperationsState
  ) {}

  /**
   * `priority` is explicit rather than implicit (LEN-2343): the Insights
   * highlights consolidate several Sections, so they fire one call per
   * selected priority in parallel and each one has to name its own. Left out,
   * it still falls back to the single Section held in `OperationsState`, which
   * is what every other caller wants.
   */
  get(
    trustNumber: string,
    reportedAt: string,
    currency?: string,
    loaderOptIn: boolean | string = true,
    priority?: number
  ) {
    const query = new QueryBuilder();
    query.equals('reported_at', reportedAt);
    // Always sent explicitly on purpose: the Insights Filter schema defaults an
    // omitted priority to 0, while Overview/Directory/Reports/Charts default an
    // omitted priority to the dynamically-resolved lowest surviving priority.
    // Never omit this param here, or Insights would silently diverge from the
    // other 4 Section endpoints for a trust whose priority-0 sibling was deleted.
    // `??`, never `||`: priority 0 is a real Section.
    query.equals(
      'priority',
      priority ?? this.operationsState.selectedPriority()
    );
    if (currency) query.equals('currency', currency);

    return this.http.get<API.Trusts.Operations.Insigths.Response>(
      platformAPI.trusts(trustNumber).operations.insights(query),
      {
        headers: new HttpHeaders({
          'Loader-Opt-In': `${loaderOptIn}`,
          'Auth-Opt-In': 'true',
        }),
        context: withCache({
          ttl: environment.CACHE_LIFETIME!,
        }),
      }
    );
  }

  accountBalance(
    trustNumber: string,
    reportedAt: string,
    currency: string,
    loaderOptIn: boolean | string = true
  ) {
    const query = new QueryBuilder();
    query.equals('reported_at', reportedAt);
    query.equals('priority', this.operationsState.selectedPriority());
    query.equals('chart', INSIGHTS_CHARTS.ACCOUNT_BALANCE);
    query.equals('currency', currency);

    return this.http.get<API.Trusts.Operations.Insigths.AccountBalance.Response>(
      platformAPI.trusts(trustNumber).operations.insights(query),
      {
        headers: new HttpHeaders({
          'Loader-Opt-In': `${loaderOptIn}`,
          'Auth-Opt-In': 'true',
        }),
        context: withCache({
          ttl: environment.CACHE_LIFETIME!,
        }),
      }
    );
  }

  cutOffs(
    trustNumber: string,
    reportedAt: string,
    loaderOptIn: boolean | string = true
  ) {
    const query = new QueryBuilder();
    query.equals('reported_at', reportedAt);
    query.equals('priority', this.operationsState.selectedPriority());
    query.equals('show_cutoff_dates', 'true');

    return this.http.get<API.Trusts.Operations.Insigths.CutOffs.Response>(
      platformAPI.trusts(trustNumber).operations.insights(query),
      {
        headers: new HttpHeaders({
          'Loader-Opt-In': `${loaderOptIn}`,
          'Auth-Opt-In': 'true',
        }),
        context: withCache({
          ttl: environment.CACHE_LIFETIME!,
        }),
      }
    );
  }

  getMatrix(
    trustNumber: string,
    reportedAt: string,
    currency?: string,
    loaderOptIn: boolean | string = true
  ) {
    const query = new QueryBuilder();
    query.equals('reported_at', reportedAt);
    if (currency) query.equals('currency', currency);
    return this.http.get<API.Trusts.Operations.Insigths.Matrix.Response>(
      platformAPI.trusts(trustNumber).operations.matrix(query),
      {
        headers: new HttpHeaders({
          'Loader-Opt-In': `${loaderOptIn}`,
          'Auth-Opt-In': 'true',
        }),
        context: withCache({
          ttl: environment.CACHE_LIFETIME!,
        }),
      }
    );
  }

  getResume(
    trustNumber: string,
    reportedAt: string,
    currency: OUT_OF_STANDARD_CURRENCY,
    loaderOptIn: boolean | string = true
  ) {
    // Deliberately not scoped by priority (LEN-1826/LEN-1833 on the backend):
    // insights-resume mixes every sibling Section on purpose.
    const params = new HttpParams({
      fromObject: {
        reported_at: reportedAt,
        currency: currency,
      },
    });

    return this.http.get<API.Trusts.Operations.Insigths.Resume.Response>(
      platformAPI.trusts(trustNumber).operations.insightsResume,
      {
        headers: new HttpHeaders({
          'Loader-Opt-In': `${loaderOptIn}`,
          'Auth-Opt-In': 'true',
        }),
        params: params,
        context: withCache({
          ttl: environment.CACHE_LIFETIME,
        }),
      }
    );
  }
}
